import {storeToRefs} from 'pinia';
import {ref, useDataTracking, useRoute, watch} from '#imports';
import {useProfilesStore} from '~/features/keyword-search/stores/profiles.store';
import {useSearchUrlStore} from '~/features/keyword-search/stores/search-url.store';
import {sanitizeSingleStringParam} from '~/utils/query-param-sanitizer';

export const SearchStartedSourceComponents = {
    category_landing_page: 'category_landing_page',
    home_unlogged: 'home_unlogged', // cms-front
    freelancer_dashboard: 'freelancer_dashboard', // dashboard-app & dashboard-freelancer-front
    navbar_search: 'navbar_search', // navbar-app & nuxt-navbar-module
    navbar_search_expanded: 'navbar_search_expanded', // navbar-app & nuxt-navbar-module
    search_block_home: 'search_block_home', // cms
    search_block_acquisition: 'search_block_acquisition', // cms
    search_block_landing_page: 'search_block_landing_page', // hopsearch-app
    search_block_footer_landing_page: 'search_block_footer_landing_page', // hopsearch-app
    search_block_sitemap: 'search_block_sitemap', // sitemap-front
    search_block_freelancer_dashboard: 'search_block_freelancer_dashboard', // dashboard-freelancer-front
    cms_search_slice: 'cms_search_slice', // cms-front
    advanced_search_dispatch: 'advanced_search_dispatch',
    advanced_search_without_ai: 'advanced_search_without_ai',
    homepage_freelance_catalog: 'homepage_freelance_catalog',
    micro_consulting_search: 'micro_consulting_search',
} as const;

type SearchSourceComponent = (typeof SearchStartedSourceComponents)[keyof typeof SearchStartedSourceComponents];

function isValidSourceComponent(sourceComponent?: SearchSourceComponent | string) {
    return sourceComponent && (Object.values(SearchStartedSourceComponents) as string[]).includes(sourceComponent);
}

export function useSearchTracking() {
    const route = useRoute();
    const {track} = useDataTracking();
    const profilesStore = useProfilesStore();
    const {searchId} = storeToRefs(profilesStore);
    const searchUrlStore = useSearchUrlStore();
    const {searchUrlParams} = storeToRefs(searchUrlStore);

    const isFirstSearch = ref<boolean>(true);
    const queryHasChanged = ref<boolean>(false);

    watch(
        () => searchUrlParams.value.query,
        (newQuery, oldQuery) => (queryHasChanged.value = sanitizeSingleStringParam(oldQuery) !== sanitizeSingleStringParam(newQuery)),
    );

    watch(
        searchId,
        (searchId) => {
            if (searchId) {
                if (queryHasChanged.value) {
                    trackSearchStarted(searchId, 'navbar_search');
                    queryHasChanged.value = false;
                } else if (isFirstSearch.value) {
                    trackSearchStarted(searchId, sanitizeSingleStringParam(route.query.sourceComponent));
                    isFirstSearch.value = false;
                }
            }
        },
        {immediate: true},
    );

    function trackSearchStarted(searchId: string, sourceComponent?: SearchSourceComponent | string) {
        track('search_started', {
            search_event_id: searchId,
            ...(isValidSourceComponent(sourceComponent) ? {source_component: sourceComponent} : {}),
        });
    }
}
